import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Heading,
  Image,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { globeImage } from '@revelio/assets';
import { NoData } from '@revelio/replots';

import rlLogo from '../../../../images/rl-logo.svg';
import {
  getSharedReport,
  type SharedReportSnapshot,
} from '../../api/shared-reports-api';
import { TextChartContent } from '../Chart/TextChart/TextChartContent';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_HEIGHT,
  REPORT_PAGE_WIDTH,
  REPORT_THEME,
} from '../PageConfigProvider/constants';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { ReportFooterPage } from '../Reports/ReportFooterPage';
import type { ChartDataEntry } from './SharedReportDataContext';
import { StaticChartRenderer } from './StaticChartRenderer';
import './shared-report.css';

const GRID_GAP = '1rem';

interface CoverParameter {
  label: string;
  value: string;
}

const halfWidth = REPORT_PAGE_WIDTH / 2;
const reportCenterPadding = `max(calc(50% - ${halfWidth}px), ${REPORT_THEME.margins.minCenterMargin}px)`;

const LABEL_COLOR = '#ffffff';
const VALUE_COLOR = '#babfc9';

const StaticCoverPage = ({
  title,
  description,
  parameters,
}: {
  title: string;
  description: string;
  parameters: CoverParameter[];
}) => {
  const topPercent = (8 / DEFAULT_GRID_ROWS) * 100;

  const mid = Math.ceil(parameters.length / 2);
  const leftColumn = parameters.slice(0, mid);
  const rightColumn = parameters.slice(mid);

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      backgroundImage="linear-gradient(to bottom, #0f1d37, #2d426a)"
      sx={{
        WebkitPrintColorAdjust: 'exact !important',
        printColorAdjust: 'exact !important',
      }}
    >
      <Image
        src={globeImage}
        alt="Report Cover"
        position="absolute"
        right={0}
        bottom={0}
        width="80%"
        maxWidth="1400px"
        height="auto"
        maxHeight="600px"
        loading="eager"
      />
      <Image
        src={rlLogo}
        alt="Revelio Labs logo"
        position="absolute"
        left={reportCenterPadding}
        top={REPORT_THEME.margins.pdf}
        height="20px"
        width="auto"
        loading="eager"
      />
      <Box
        position="absolute"
        left={reportCenterPadding}
        right={reportCenterPadding}
        top={`${topPercent}%`}
        zIndex={1}
        color={LABEL_COLOR}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
      >
        <Heading as="h1" size="2xl" fontWeight="bold" marginBottom="1rem">
          {title || 'Title'}
        </Heading>
        <Text
          fontSize="md"
          marginTop={title ? '0.5rem' : '0'}
          color={VALUE_COLOR}
        >
          {description || 'Description'}
        </Text>
        {parameters.length > 0 && (
          <Flex
            flexDirection="row"
            alignItems="flex-start"
            gap="1.5rem"
            height="44px"
            marginTop="1.5rem"
          >
            <Box minWidth={0}>
              {leftColumn.map((param) => (
                <Box
                  key={param.label}
                  marginBottom={rightColumn.length > 0 ? '0.75rem' : 0}
                >
                  <Text fontSize="sm" fontWeight="bold" color={LABEL_COLOR}>
                    {param.label}
                  </Text>
                  <Text fontSize="sm" color={VALUE_COLOR}>
                    {param.value}
                  </Text>
                </Box>
              ))}
            </Box>
            {rightColumn.length > 0 && (
              <>
                <Divider
                  orientation="vertical"
                  color="white"
                  marginX="1.5rem"
                />
                <Box minWidth={0}>
                  {rightColumn.map((param) => (
                    <Box key={param.label} marginBottom="0.75rem">
                      <Text fontSize="sm" fontWeight="bold" color={LABEL_COLOR}>
                        {param.label}
                      </Text>
                      <Text fontSize="sm" color={VALUE_COLOR}>
                        {param.value}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

interface SharedReportContentProps {
  snapshot: SharedReportSnapshot;
}

const SharedReportContent = ({ snapshot }: SharedReportContentProps) => {
  const pageConfig = snapshot.pageConfig as {
    name: string;
    _resolvedCoverParameters?: CoverParameter[];
    tabs?: Array<{
      tabContent?: {
        items?: PositionedItem[];
        grid?: { rows?: number; columns?: number };
        title?: string;
        description?: string;
      };
    }>;
  };

  const tabContent = pageConfig.tabs?.[0]?.tabContent;
  const items = tabContent?.items ?? [];
  const gridRows = tabContent?.grid?.rows ?? DEFAULT_GRID_ROWS;
  const gridColumns = tabContent?.grid?.columns ?? DEFAULT_GRID_COLUMNS;
  const chartData = snapshot.chartData as Record<string, ChartDataEntry>;

  const numPages = Math.max(1, Math.ceil(gridRows / DEFAULT_GRID_ROWS));
  const totalHeight = numPages * REPORT_PAGE_HEIGHT;

  const pageDividers = Array.from({ length: numPages }).map((_, index) => (
    <Divider
      key={`divider-${index}`}
      position="absolute"
      top={`${(index + 1) * REPORT_PAGE_HEIGHT}px`}
      left="0"
      right="0"
    />
  ));

  return (
    <Box
      position="relative"
      width="100%"
      height={`${totalHeight}px`}
      backgroundColor="white"
      borderRadius="lg"
    >
      {/* Cover page */}
      <Box width="100%" height={`${REPORT_PAGE_HEIGHT}px`} position="absolute">
        <StaticCoverPage
          title={tabContent?.title ?? pageConfig.name ?? ''}
          description={tabContent?.description ?? ''}
          parameters={pageConfig._resolvedCoverParameters ?? []}
        />
      </Box>

      {/* Footer page */}
      <Box
        width="100%"
        height={`${REPORT_PAGE_HEIGHT}px`}
        position="absolute"
        top={`${(numPages - 1) * REPORT_PAGE_HEIGHT}px`}
      >
        <ReportFooterPage />
      </Box>

      {/* Chart grid */}
      <Box
        display="grid"
        position="relative"
        gap={GRID_GAP}
        width="100%"
        height={`${totalHeight}px`}
        paddingLeft={reportCenterPadding}
        paddingRight={reportCenterPadding}
        style={{
          gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
        }}
      >
        {items.map((item) => {
          const entry = chartData[item.id];

          const itemStyle = {
            gridColumn: `${item.position.x + 1} / span ${item.position.width}`,
            gridRow: `${item.position.y + 1} / span ${item.position.height}`,
          };

          if (item.plot?.chartType === 'text') {
            return (
              <Box
                key={item.id}
                style={itemStyle}
                minWidth="0"
                minHeight="0"
                width="100%"
                height="100%"
              >
                <Card width="100%" height="100%" variant="unstyled">
                  <CardBody padding="0.5rem 14px" height="100%" overflow="auto">
                    <TextChartContent
                      content={item.plot?.textHtml ?? ''}
                      color={item.plot?.color}
                      vAlign={item.plot?.textVAlign}
                    />
                  </CardBody>
                </Card>
              </Box>
            );
          }

          if (!entry?.component) {
            return (
              <Box
                key={item.id}
                style={itemStyle}
                minWidth="0"
                minHeight="0"
                width="100%"
                height="100%"
              >
                <Card width="100%" height="100%" variant="unstyled">
                  <CardBody
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <NoData />
                  </CardBody>
                </Card>
              </Box>
            );
          }

          return (
            <Box
              key={item.id}
              style={itemStyle}
              minWidth="0"
              minHeight="0"
              width="100%"
              height="100%"
              {...(item.plot?.color === '#ffffff' && { zIndex: 1 })}
            >
              <Card width="100%" height="100%" variant="unstyled">
                {item.title && (
                  <CardHeader padding="0.5rem 14px 0">
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                      {item.title}
                    </Text>
                  </CardHeader>
                )}
                <CardBody
                  padding="0.5rem 14px"
                  height={item.title ? 'calc(100% - 40px)' : '100%'}
                  minHeight={0}
                >
                  <StaticChartRenderer chartDataEntry={entry} />
                </CardBody>
              </Card>
            </Box>
          );
        })}
        {pageDividers}
      </Box>
    </Box>
  );
};

export const SharedReportViewer = () => {
  const { token } = useParams<{ token: string }>();

  const {
    data: snapshot,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['shared-report', token],
    queryFn: () => getSharedReport(token ?? ''),
    enabled: !!token,
    retry: 1,
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <Flex direction="column" alignItems="center" gap={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading shared report...</Text>
        </Flex>
      </Flex>
    );
  }

  if (error || !snapshot) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <Flex direction="column" alignItems="center" gap={4}>
          <Heading size="md" color="gray.700">
            Report not found
          </Heading>
          <Text color="gray.500">
            This shared report link may be invalid or the report may have been
            removed.
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <div className="shared-report-page">
      <SharedReportContent snapshot={snapshot} />
    </div>
  );
};
